// جدول داده با صفحه‌بندی Cursor-based (CLAUDE.md §6: ?cursor=&limit=)، مرتب‌سازی، فیلتر، حالت خالی و اسکلتون.
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { useTranslation } from '@phoenix/i18n';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { EmptyState, ErrorState } from './states';

export type SortDirection = 'asc' | 'desc';

export interface DataTableSort {
  by: string;
  direction: SortDirection;
}

export interface DataTableColumn<T> {
  id: string;
  headerKey: string;
  cell: (row: T) => ReactNode;
  sortable?: boolean;
  align?: 'left' | 'right' | 'center';
  width?: number | string;
}

export interface DataTableProps<T> {
  columns: readonly DataTableColumn<T>[];
  rows: readonly T[] | undefined;
  getRowId: (row: T) => string;
  isLoading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  sort?: DataTableSort;
  onSortChange?: (sort: DataTableSort) => void;
  /** نوار فیلتر بالای جدول. */
  toolbar?: ReactNode;
  /** Cursor بعدی وجود دارد (nextCursor != null). */
  hasMore?: boolean;
  onLoadMore?: () => void;
  isFetchingMore?: boolean;
  emptyTitleKey?: string;
  emptyDescriptionKey?: string;
  emptyAction?: ReactNode;
  onRowClick?: (row: T) => void;
  /** برچسب دسترس‌پذیر جدول. */
  captionKey: string;
  skeletonRows?: number;
}

export function DataTable<T>({
  columns,
  rows,
  getRowId,
  isLoading = false,
  error,
  onRetry,
  sort,
  onSortChange,
  toolbar,
  hasMore = false,
  onLoadMore,
  isFetchingMore = false,
  emptyTitleKey,
  emptyDescriptionKey,
  emptyAction,
  onRowClick,
  captionKey,
  skeletonRows = 5,
}: DataTableProps<T>) {
  const { t } = useTranslation('common');

  const header = (
    <TableHead>
      <TableRow>
        {columns.map((column) => (
          <TableCell
            key={column.id}
            align={column.align}
            sx={{ width: column.width, fontWeight: 600 }}
            sortDirection={sort?.by === column.id ? sort.direction : false}
          >
            {column.sortable && onSortChange ? (
              <TableSortLabel
                active={sort?.by === column.id}
                direction={sort?.by === column.id ? sort.direction : 'asc'}
                onClick={() =>
                  onSortChange({
                    by: column.id,
                    direction: sort?.by === column.id && sort.direction === 'asc' ? 'desc' : 'asc',
                  })
                }
              >
                {t(column.headerKey)}
              </TableSortLabel>
            ) : (
              t(column.headerKey)
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );

  const container = useRef<HTMLDivElement | null>(null);
  const [scrollable, setScrollable] = useState(false);
  useEffect(() => {
    const element = container.current;
    if (!element) {
      return undefined;
    }
    const measure = () => setScrollable(element.scrollWidth > element.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    const table = element.firstElementChild;
    if (table) {
      observer.observe(table);
    }
    return () => observer.disconnect();
  });

  let body: ReactNode;
  if (isLoading) {
    body = Array.from({ length: skeletonRows }, (_, i) => (
      <TableRow key={`s-${i}`}>
        {columns.map((column) => (
          <TableCell key={column.id}>
            <Skeleton />
          </TableCell>
        ))}
      </TableRow>
    ));
  } else if (rows && rows.length > 0) {
    body = rows.map((row) => (
      <TableRow
        key={getRowId(row)}
        hover={Boolean(onRowClick)}
        onClick={onRowClick ? () => onRowClick(row) : undefined}
        onKeyDown={
          onRowClick
            ? (event) => {
                if (event.key === 'Enter') {
                  onRowClick(row);
                }
              }
            : undefined
        }
        tabIndex={onRowClick ? 0 : undefined}
        sx={onRowClick ? { cursor: 'pointer' } : undefined}
      >
        {columns.map((column) => (
          <TableCell key={column.id} align={column.align}>
            {column.cell(row)}
          </TableCell>
        ))}
      </TableRow>
    ));
  }

  return (
    <Paper variant="outlined" data-testid="data-table" sx={{ overflow: 'hidden' }}>
      {toolbar && <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>{toolbar}</Box>}
      {error && !isLoading ? (
        <Box sx={{ p: 2 }}>
          <ErrorState error={error} onRetry={onRetry} compact />
        </Box>
      ) : !isLoading && (!rows || rows.length === 0) ? (
        <EmptyState titleKey={emptyTitleKey} descriptionKey={emptyDescriptionKey} action={emptyAction} />
      ) : (
        <TableContainer
          ref={container}
          // جدول پهن‌تر از صفحه با صفحه‌کلید پیمایش‌پذیر است (axe scrollable-region-focusable — C-AGENT-1454).
          {...(scrollable ? { tabIndex: 0, role: 'region', 'aria-label': t(captionKey) } : {})}
        >
          <Table aria-label={t(captionKey)} aria-busy={isLoading}>
            {header}
            <TableBody>{body}</TableBody>
          </Table>
        </TableContainer>
      )}
      {hasMore && onLoadMore && !error && (
        <Stack alignItems="center" sx={{ p: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={onLoadMore} disabled={isFetchingMore} data-testid="data-table-load-more">
            {t(isFetchingMore ? 'common:states.loading' : 'common:table.load_more')}
          </Button>
        </Stack>
      )}
    </Paper>
  );
}
